<script lang="ts">
	import { Card, Button, Label, Input, Checkbox, Spinner } from 'flowbite-svelte';
	import MetricChart from './MetricChart.svelte';
	import { getProbe, getProbeMetricSeries } from '../../AppNetwork';
	import type { ProbeMetric, MetricSeries } from '../../AppTypes';

	export let probeId: number;

	// Built-in metrics every probe collects (incl. http-status probes).
	const BUILTINS: { key: string; label: string }[] = [
		{ key: '$up', label: 'Availability' },
		{ key: '$responseTime', label: 'Response time' }
	];

	const RANGES: { key: string; label: string; ms: number }[] = [
		{ key: '1h', label: '1h', ms: 60 * 60 * 1000 },
		{ key: '6h', label: '6h', ms: 6 * 60 * 60 * 1000 },
		{ key: '24h', label: '24h', ms: 24 * 60 * 60 * 1000 },
		{ key: '7d', label: '7d', ms: 7 * 24 * 60 * 60 * 1000 },
		{ key: '30d', label: '30d', ms: 30 * 24 * 60 * 60 * 1000 }
	];

	let metricDefs: ProbeMetric[] = [];
	let selectedKeys: string[] = [];
	let rangeKey = '24h';
	let customFrom = '';
	let customTo = '';
	let series: MetricSeries[] = [];
	let loading = true;
	let error = '';
	let loadedProbe: number | null = null;

	async function loadDefs() {
		loading = true;
		error = '';
		try {
			const probe = await getProbe(probeId);
			metricDefs = probe.metrics ?? [];
			selectedKeys = [...BUILTINS.map((b) => b.key), ...metricDefs.map((m) => m.key)];
			await loadSeries();
		} catch (e) {
			error = e instanceof Error ? e.message : 'Failed to load metrics';
		} finally {
			loading = false;
		}
	}

	// (Re)load whenever the target probe changes — also covers initial mount.
	$: if (probeId !== loadedProbe) {
		loadedProbe = probeId;
		void loadDefs();
	}

	$: metricOptions = [...BUILTINS, ...metricDefs.map((m) => ({ key: m.key, label: m.label }))];

	async function loadSeries() {
		loading = true;
		error = '';
		try {
			let from: string | undefined;
			let to: string | undefined;
			if (rangeKey === 'custom') {
				from = customFrom ? new Date(customFrom).toISOString() : undefined;
				to = customTo ? new Date(customTo).toISOString() : undefined;
			} else {
				const ms = RANGES.find((r) => r.key === rangeKey)!.ms;
				from = new Date(Date.now() - ms).toISOString();
				to = new Date().toISOString();
			}
			const result = await getProbeMetricSeries(probeId, { from, to, keys: selectedKeys });
			series = result.series;
		} catch (e) {
			error = e instanceof Error ? e.message : 'Failed to load metrics';
		} finally {
			loading = false;
		}
	}

	function setRange(key: string) {
		rangeKey = key;
		void loadSeries();
	}

	function toggleKey(key: string, checked: boolean) {
		selectedKeys = checked ? [...selectedKeys, key] : selectedKeys.filter((k) => k !== key);
		void loadSeries();
	}
</script>

<div class="space-y-4">
	<Card class="max-w-none">
		<div class="flex flex-wrap items-end gap-4">
			<div>
				<Label class="mb-1">Range</Label>
				<div class="flex gap-1">
					{#each RANGES as r}
						<Button size="xs" color={rangeKey === r.key ? 'primary' : 'alternative'} on:click={() => setRange(r.key)}>{r.label}</Button>
					{/each}
					<Button size="xs" color={rangeKey === 'custom' ? 'primary' : 'alternative'} on:click={() => setRange('custom')}>Custom</Button>
				</div>
			</div>

			{#if rangeKey === 'custom'}
				<div><Label class="mb-1">From</Label><Input type="datetime-local" bind:value={customFrom} /></div>
				<div><Label class="mb-1">To</Label><Input type="datetime-local" bind:value={customTo} /></div>
				<Button on:click={loadSeries}><i class="fa-solid fa-magnifying-glass me-2"></i>Apply</Button>
			{/if}
		</div>

		<div class="mt-4 border-t border-gray-200 dark:border-gray-700 pt-3">
			<Label class="mb-1">Metrics</Label>
			<div class="flex flex-wrap gap-x-6 gap-y-1 mt-1">
				{#each metricOptions as opt}
					<Checkbox checked={selectedKeys.includes(opt.key)} on:change={(e) => toggleKey(opt.key, e.currentTarget.checked)}>
						{opt.label}
						{#if opt.key.startsWith('$')}<span class="text-gray-400 text-xs ml-1">(built-in)</span>{:else}<span class="text-gray-400 text-xs ml-1">{opt.key}</span>{/if}
					</Checkbox>
				{/each}
			</div>
		</div>
	</Card>

	{#if loading}
		<div class="flex justify-center py-12"><Spinner size="8" /></div>
	{:else if error}
		<Card class="max-w-none"><p class="text-red-500 text-center py-4">{error}</p></Card>
	{:else if series.length === 0}
		<Card class="max-w-none"><p class="text-gray-400 text-center py-8">Select one or more metrics to plot.</p></Card>
	{:else}
		<div class="space-y-4">
			{#each series as s (s.key)}
				<MetricChart series={s} />
			{/each}
		</div>
	{/if}
</div>
